import { z } from "zod";
export const kitSchemaVersion = 1;
export const entryCollectionSchema = z.enum(["technical", "english"]);
export const kitEntryKindSchema = z.enum([
    "concept",
    "term",
    "phrase",
    "sentence",
    "formula",
    "mixed",
    "error-message",
]);
export const kitTitleFormatSchema = z.enum(["plain", "latex", "mixed-markdown"]);
export const kitKnowledgeNatureSchema = z.enum([
    "stable-concept",
    "dynamic-fact",
    "working-hypothesis",
    "unknown-boundary",
]);
export const kitPrimaryCategorySchema = z.enum([
    "软件开发",
    "系统、基础设施与运维",
    "人工智能与数据",
    "产品、设计与运营",
    "数学与统计",
    "游戏开发",
    "视觉、视频与音频",
    "商业与金融",
    "通用思维与方法",
    "日常生活与沟通",
    "学习与研究",
    "人工智能与计算机",
    "自然科学",
    "商业、金融与职业",
    "社会、人文与法律",
    "描述、情感与认知",
    "其他／待归类",
]);
export const kitBlockDraftSchema = z.object({
    id: z.string().trim().min(1).max(120),
    kind: z.enum(["markdown", "code", "math"]),
    title: z.string().trim().min(1).max(160),
    body: z.string().trim().min(1).max(40_000),
    language: z.string().trim().max(80).nullable(),
    order: z.number().int().nonnegative(),
    knowledgeNature: kitKnowledgeNatureSchema,
    sourceUrl: z.string().url().nullable().default(null),
    verifiedAt: z.string().datetime().nullable().default(null),
});
export const kitCandidateSchema = z.object({
    candidateId: z.string().uuid(),
    name: z.string().trim().min(1).max(500),
    titleFormat: kitTitleFormatSchema,
    entryKind: kitEntryKindSchema,
    collection: entryCollectionSchema.default("technical"),
    aliases: z.array(z.string().trim().min(1).max(500)).max(20).default([]),
    sourceExcerpt: z.string().trim().min(1).max(12_000),
    whyDifficult: z.string().trim().min(1).max(2_000),
    primaryCategory: kitPrimaryCategorySchema,
    tags: z.array(z.string().trim().min(1).max(80)).max(24).default([]),
    confidence: z.number().min(0).max(1),
});
export const kitPublishEntrySchema = kitCandidateSchema.omit({
    sourceExcerpt: true,
    whyDifficult: true,
    confidence: true,
}).extend({
    summary: z.string().trim().min(1).max(2_000),
    context: z.string().trim().min(1).max(8_000),
    blocks: z.array(kitBlockDraftSchema).min(1).max(24),
}).superRefine((entry, context) => {
    const blockIds = new Set();
    for (const block of entry.blocks) {
        if (blockIds.has(block.id)) {
            context.addIssue({
                code: "custom",
                path: ["blocks"],
                message: "同一词条的内容块 id 必须唯一",
            });
        }
        blockIds.add(block.id);
    }
});
export const kitSnapshotBlockSchema = z.object({
    id: z.string(),
    kind: z.enum(["markdown", "code", "math"]),
    title: z.string(),
    body: z.string(),
    language: z.string().nullable(),
    order: z.number().int().nonnegative(),
    knowledgeNature: kitKnowledgeNatureSchema.default("stable-concept"),
    sourceUrl: z.string().url().nullable().default(null),
    verifiedAt: z.string().datetime().nullable().default(null),
});
export const kitSnapshotTermSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    titleFormat: kitTitleFormatSchema.default("plain"),
    entryKind: kitEntryKindSchema.default("concept"),
    collection: entryCollectionSchema.default("technical"),
    aliases: z.array(z.string()).default([]),
    summary: z.string(),
    category: z.string(),
    tags: z.array(z.string()).default([]),
    context: z.string(),
    currentRevision: z.object({ id: z.string().uuid(), version: z.number().int().positive() }),
    blocks: z.array(kitSnapshotBlockSchema),
    updatedAt: z.string().datetime(),
});
export const kitSnapshotSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    capabilities: z.object({
        entryCollections: z.boolean(),
    }).default({ entryCollections: false }),
    generatedAt: z.string().datetime(),
    terms: z.array(kitSnapshotTermSchema),
});
export const kitSnapshotQuerySchema = z.object({
    collection: z.union([entryCollectionSchema, z.literal("all")]).default("technical"),
});
export const kitTermResolveRequestSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    collection: entryCollectionSchema,
    names: z.array(z.string().trim().min(1).max(500)).min(1).max(100),
}).strict();
export const kitTermReadRequestSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    collection: entryCollectionSchema,
    termIds: z.array(z.string().uuid()).min(1).max(50),
}).strict();
export const kitTermSliceSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    generatedAt: z.string().datetime(),
    terms: z.array(kitSnapshotTermSchema),
});
export const kitImportDecisionSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("create") }),
    z.object({ action: z.literal("separate-sense") }),
    z.object({
        action: z.literal("update"),
        targetTermId: z.string().uuid(),
        baselineRevisionId: z.string().uuid(),
    }),
]);
export const kitImportRequestSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    runId: z.string().uuid(),
    items: z.array(z.object({
        entry: kitPublishEntrySchema,
        decision: kitImportDecisionSchema,
    })).min(1).max(50),
}).superRefine((request, context) => {
    const candidateIds = new Set();
    for (const [index, item] of request.items.entries()) {
        if (candidateIds.has(item.entry.candidateId)) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "entry", "candidateId"],
                message: "同一批导入不能重复包含同一个候选词条",
            });
        }
        candidateIds.add(item.entry.candidateId);
    }
});
export const kitImportReceiptSchema = z.object({
    candidateId: z.string().uuid(),
    action: z.enum(["create", "separate-sense", "update"]),
    termId: z.string().uuid(),
    revisionId: z.string().uuid(),
    version: z.number().int().positive(),
});
export const kitImportResponseSchema = z.object({
    schemaVersion: z.literal(kitSchemaVersion),
    runId: z.string().uuid(),
    importedAt: z.string().datetime(),
    receipts: z.array(kitImportReceiptSchema),
});
